import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { BlackBar, Menu } from 'components';
import { Link } from 'gatsby';
import { motion } from 'framer-motion';
import { isWindows } from 'react-device-detect';
import AgencyXLogo from 'images/shared/AgencyXLogo';
import { useLocation } from '@reach/router';

const Header = ({ pathname }) => {
  const location = useLocation();
  const invertColors =
    location.pathname === '/' ||
    location.pathname === '/services/healthcare-event-management/' ||
    location.pathname === '/services/web-development/' ||
    location.pathname === '/services/creative-and-design/' ||
    location.pathname === '/services/healthcare-brand-and-campaign-strategy/' ||
    location.pathname === '/market-faster-b2b/' ||
    location.pathname === '/market-faster-b2c/' ||
    location.pathname === '/market-faster/get-broadcasting/';
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [scrollYPosition, setScrollYPosition] = useState(0);

  useEffect(() => {
    setScrollYPosition(window.scrollY);
    const id = window.addEventListener('scroll', () => {
      setScrollYPosition(window.scrollY);
    });
    return () => window.removeEventListener('scroll', id);
  }, []);

  useEffect(() => {
    if (!isWindows && menuIsOpen) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = 'scroll';
    }
  }, [menuIsOpen]);

  const userHasScrolled = scrollYPosition > 0;

  // Get all focusable elements in the header
  const getFocusableElements = () => {
    const header = document.querySelector('header');
    return Array.from(header.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])'));
  };

  const handleTabKey = (e) => {
    if (e.key !== 'Tab') return;

    const focusableElements = getFocusableElements();
    const lastElement = focusableElements[focusableElements.length - 1];
    const firstElement = focusableElements[1];

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else if (document.activeElement === lastElement) {
      e.preventDefault();
      firstElement.focus();
    }
  };

  useEffect(() => {
    if (menuIsOpen) {
      document.addEventListener('keydown', handleTabKey);
    } else {
      document.removeEventListener('keydown', handleTabKey);
    }
  }, [menuIsOpen]);

  return (
    <Container
      pathname={location.pathname}
      variants={headerVariants}
      initial="userIsAtTopOfPage"
      animate={userHasScrolled ? 'userHasScrolled' : 'userIsAtTopOfPage'}>
      <div>
        <Link
          to="/"
          aria-label="Homepage"
          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          tabIndex={menuIsOpen ? -1 : 0}
          aria-hidden={menuIsOpen}>
          <StyledAgencyXLogo
            $invertColors={invertColors}
            $menuIsOpen={menuIsOpen}
            $userHasScrolled={userHasScrolled}
            $scrollPosition={scrollYPosition}
            aria-label="Agency X"
          />
        </Link>
        <IncreasedTapArea
          onClick={() => setMenuIsOpen(!menuIsOpen)}
          aria-label={menuIsOpen ? 'Close Navigation Menu' : 'Open Navigation Menu'}
          type="button">
          <BlackBars pathname={pathname} aria-hidden="true">
            <BlackBar
              variants={topBlackBarVariants}
              menuIsOpen={menuIsOpen}
              invertColors={invertColors}
              userHasScrolled={userHasScrolled}
            />
            <BlackBar
              variants={bottomBlackBarVariants}
              menuIsOpen={menuIsOpen}
              invertColors={invertColors}
              userHasScrolled={userHasScrolled}
            />
          </BlackBars>
        </IncreasedTapArea>
        <Menu
          menuIsOpen={menuIsOpen}
          setMenuIsOpen={setMenuIsOpen}
          userHasScrolled={userHasScrolled}
        />
      </div>
    </Container>
  );
};

const headerVariants = {
  userIsAtTopOfPage: {
    backgroundColor: 'rgba(000, 000, 000, 0)',
    height: '6.25rem',
    transition: {
      type: 'tween',
      ease: 'easeInOut',
      duration: 0.375,
      height: {
        delay: 0
      }
    }
  },
  userHasScrolled: {
    backgroundColor: 'rgba(000, 000, 000, 0.725)',
    height: '4rem',
    transition: {
      type: 'tween',
      ease: 'easeInOut',
      duration: 0.375
    }
  }
};

/* TODO: DRY Refactor the BlackBar animnation implementation. */
const topBlackBarVariants = {
  menuIsClosed: {
    backgroundColor: '#000',
    clipPath: 'polygon(0% 100%, 7.5% 0%, 100% 0%, 100% 100%)',
    width: '0.875rem',
    x: 6,
    rotate: 0,
    transition: {
      ease: 'easeInOut'
    }
  },
  menuIsClosedAndUserHasScrolled: {
    backgroundColor: '#fff',
    clipPath: 'polygon(0% 100%, 7.5% 0%, 100% 0%, 100% 100%)',
    width: '0.875rem',
    x: 6,
    rotate: 0,
    transition: {
      ease: 'easeInOut'
    }
  },
  menuIsOpen: {
    backgroundColor: '#000',
    clipPath: 'polygon(0% 0%, 0% 100%, 100% 100%, 100% 0%)',
    width: '1.1rem',
    transformOrigin: 'top left',
    x: 0,
    rotate: 45,
    transition: {
      ease: 'easeInOut'
    }
  }
};

const bottomBlackBarVariants = {
  menuIsClosed: {
    backgroundColor: '#000',
    width: '0.875rem',
    clipPath: 'polygon(0% 100%, 0% 0%, 100% 0%, 92.5% 100%)',
    rotate: 0,
    transition: {
      ease: 'easeInOut'
    }
  },
  menuIsClosedAndUserHasScrolled: {
    backgroundColor: '#fff',
    width: '0.875rem',
    clipPath: 'polygon(0% 100%, 0% 0%, 100% 0%, 92.5% 100%)',
    rotate: 0,
    transition: {
      ease: 'easeInOut'
    }
  },
  menuIsOpen: {
    backgroundColor: '#000',
    clipPath: 'polygon(0% 0%, 0% 100%, 100% 100%, 100% 0%)',
    width: '1.1rem',
    transformOrigin: 'bottom left',
    rotate: -45,
    transition: {
      ease: 'easeInOut'
    }
  }
};

const Container = styled(motion.header)`
  grid-area: 1 / 2;
  z-index: 3;
  opacity: ${({ pathname }) => (pathname === '/logobook/' ? 0 : 1)} !important;
  position: fixed;
  width: 100%;
  > div {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: space-between;
    margin: 0 auto;
    padding: 0 1.25rem;

    @media screen and (min-width: 48rem) {
      padding: 0 6.25%;
    }

    @media screen and (min-width: 90rem) {
      padding: 0 9%;
    }

    @media screen and (min-width: 120rem) {
      max-width: 1562px;
      padding: 0;
    }
  }
`;

const StyledAgencyXLogo = styled(AgencyXLogo)`
  position: relative;
  z-index: 2;
  padding: 0.2em 0;
  width: 10rem;
  transition-duration: 0.75s;
  opacity: ${({ $invertColors, $menuIsOpen, $scrollPosition }) =>
    $invertColors && !$menuIsOpen && !$scrollPosition > 0 ? 0 : 1};
  path {
    fill: ${({ $invertColors, $userHasScrolled, $menuIsOpen }) =>
      ($invertColors && !$menuIsOpen) || ($userHasScrolled && !$menuIsOpen) ? 'white' : 'black'};
    &:nth-child(2) {
      fill: ${({ $menuIsOpen }) => ($menuIsOpen ? 'white' : 'var(--ax-gold)')};
    }
  }
`;

const IncreasedTapArea = styled.button`
  align-items: center;
  cursor: pointer;
  display: flex;
  height: 2.625rem;
  justify-content: center;
  left: 0.688rem;
  position: relative;
  width: 2.625rem;
  background: transparent;
  z-index: 3;
`;

const BlackBars = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 0.875rem;
  justify-content: space-between;
  position: relative;
  pointer-events: none;
  width: 1.25rem;
  opacity: 1;
`;

export default Header;
